import { useState, useMemo } from 'react';
import { Calculator } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

interface MortgageCalculatorProps {
  className?: string;
}

export function MortgageCalculator({ className }: MortgageCalculatorProps) {
  const [homePrice, setHomePrice] = useState(400000);
  const [downPayment, setDownPayment] = useState(80000);
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTerm, setLoanTerm] = useState(30);

  const downPaymentPercent = useMemo(() => {
    return homePrice > 0 ? ((downPayment / homePrice) * 100).toFixed(1) : '0';
  }, [homePrice, downPayment]);

  const monthlyPayment = useMemo(() => {
    const principal = homePrice - downPayment;
    if (principal <= 0 || interestRate <= 0) return 0;
    
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                   (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    return isNaN(payment) || !isFinite(payment) ? 0 : payment;
  }, [homePrice, downPayment, interestRate, loanTerm]);

  const totalInterest = useMemo(() => {
    const principal = homePrice - downPayment;
    const totalPaid = monthlyPayment * loanTerm * 12;
    return totalPaid - principal;
  }, [homePrice, downPayment, monthlyPayment, loanTerm]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleHomePriceChange = (value: string) => {
    const numValue = parseInt(value.replace(/[^0-9]/g, ''), 10) || 0;
    setHomePrice(numValue);
    // Adjust down payment to maintain percentage if it exceeds home price
    if (downPayment > numValue) {
      setDownPayment(Math.round(numValue * 0.2));
    }
  };

  const handleDownPaymentChange = (value: string) => {
    const numValue = parseInt(value.replace(/[^0-9]/g, ''), 10) || 0;
    setDownPayment(Math.min(numValue, homePrice));
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calculator className="h-5 w-5 text-primary" />
          Mortgage Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Home Price */}
        <div className="space-y-2">
          <Label htmlFor="home-price" className="text-sm font-medium">
            Home Price
          </Label>
          <Input
            id="home-price"
            type="text"
            value={formatCurrency(homePrice)}
            onChange={(e) => handleHomePriceChange(e.target.value)}
            className="text-right font-medium"
          />
          <Slider
            value={[homePrice]}
            onValueChange={([value]) => {
              setHomePrice(value);
              if (downPayment > value) setDownPayment(Math.round(value * 0.2));
            }}
            min={50000}
            max={2000000}
            step={10000}
            className="mt-2"
          />
        </div>

        {/* Down Payment */}
        <div className="space-y-2">
          <Label htmlFor="down-payment" className="text-sm font-medium">
            Down Payment ({downPaymentPercent}%)
          </Label>
          <Input
            id="down-payment"
            type="text"
            value={formatCurrency(downPayment)}
            onChange={(e) => handleDownPaymentChange(e.target.value)}
            className="text-right font-medium"
          />
          <Slider
            value={[downPayment]}
            onValueChange={([value]) => setDownPayment(value)}
            min={0}
            max={homePrice}
            step={5000}
            className="mt-2"
          />
        </div>

        {/* Interest Rate */}
        <div className="space-y-2">
          <Label htmlFor="interest-rate" className="text-sm font-medium">
            Interest Rate ({interestRate.toFixed(2)}%)
          </Label>
          <Slider
            value={[interestRate]}
            onValueChange={([value]) => setInterestRate(value)}
            min={1}
            max={12}
            step={0.125}
          />
        </div>

        {/* Loan Term */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Loan Term</Label>
          <div className="flex gap-2">
            {[15, 20, 30].map((term) => (
              <button
                key={term}
                type="button"
                onClick={() => setLoanTerm(term)}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-md border transition-colors ${
                  loanTerm === term
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-input hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                {term} yr
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="pt-4 border-t space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground text-sm">Monthly Payment</span>
            <span className="text-2xl font-bold text-primary">
              {formatCurrency(monthlyPayment)}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Loan Amount</span>
            <span className="font-medium">{formatCurrency(homePrice - downPayment)}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Total Interest</span>
            <span className="font-medium">{formatCurrency(totalInterest)}</span>
          </div>
        </div>

        <p className="text-xs text-muted-foreground pt-2">
          * Estimate only. Does not include taxes, insurance, or PMI.
        </p>
      </CardContent>
    </Card>
  );
}
